/**
 * Reset Password Page
 * Route: /reset-password?token=xxx
 * Password must be repeated. Copy-paste disabled on confirm field.
 */
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, Lock } from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, password_confirm: passwordConfirm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4">
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-[#E5E5E5] mb-4">Lien de réinitialisation invalide.</p>
            <Link to="/forgot-password">
              <Button className="bg-[#C9A24D] hover:bg-[#d4af5a] text-black">Demander un nouveau lien</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4">
      <Link to="/login" className="absolute top-6 left-6 flex items-center gap-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] text-sm">
        <ArrowLeft className="w-4 h-4" /> Retour à la connexion
      </Link>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#C9A24D] mb-2">Y GO</h1>
          <p className="text-[#E5E5E5] text-sm tracking-widest uppercase">Shuttle Services France</p>
        </div>

        <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 shadow-2xl">
          <CardHeader className="pb-4 text-center">
            <CardTitle className="text-xl text-[#E5E5E5]">
              {success ? 'Mot de passe réinitialisé' : 'Nouveau mot de passe'}
            </CardTitle>
            <CardDescription className="text-[#E5E5E5]/60">
              {success ? '' : 'Choisissez un nouveau mot de passe sécurisé'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center py-4" data-testid="reset-success">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-[#E5E5E5] text-sm mb-4">Votre mot de passe a été réinitialisé avec succès.</p>
                <Link to="/login">
                  <Button className="bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="go-to-login">
                    Se connecter
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="reset-error">
                    <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                  </div>
                )}
                <div>
                  <Label className="text-[#E5E5E5]">Nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Min. 8 caractères, 1 maj., 1 chiffre"
                      className="pl-9 pr-10 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#555]"
                      data-testid="reset-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#999]">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5]">Confirmer le mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      onPaste={(e) => e.preventDefault()}
                      onCopy={(e) => e.preventDefault()}
                      required
                      minLength={8}
                      placeholder="Retapez votre mot de passe"
                      className="pl-9 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#555]"
                      data-testid="reset-password-confirm-input"
                    />
                  </div>
                  <p className="text-[#666] text-xs mt-1">Le copier-coller est désactivé sur ce champ</p>
                </div>
                {password && passwordConfirm && password !== passwordConfirm && (
                  <p className="text-red-400 text-xs">Les mots de passe ne correspondent pas</p>
                )}
                <Button type="submit" disabled={loading || !password || !passwordConfirm} className="w-full bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="reset-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
